import React, { useEffect, useState } from 'react';
import { VisualizeEngine } from '@tier4/autowareviz-core';
import { SemanticType, TrackedObject } from '@tier4/autowareviz-messages';
import { AutowareViz, useMessageCatalog } from '@tier4/autowareviz-react';
import { Space, Model as VehicleObject, Coordinate, Camera, CamMode, DetectedObjects, Route, Path, Trajectory, PredictedPaths, VirtualWalls, GroundGrid } from '@tier4/autowareviz-react-threejs';
import { Lanelet2MapsGroup, loadLanelet2Map } from '@tier4/autowareviz-map';
//import milee from './milee.gltf';
import j6 from '../../resources/models/BYDJ6_NPC.dae';
import styled from '@emotion/styled';
import { OrbitControls } from '@react-three/drei';
import { RemoteClock } from './RemoteClock';
import { DirectionalTrackedObjects } from './DirectionalDynamicObjects';
import { Lanelet2Map } from './Lanelet2Map';
import { Backdrop, CircularProgress, Grid, Typography } from '@mui/material';

const StyledRemoteClock = styled(RemoteClock)({
    position: 'absolute',
    top: 0,
    left: 0,
    margin: '1ex',
    color: 'white',
});

function semanticText(semantic: SemanticType) {
    switch (semantic) {
        case SemanticType.UNKNOWN:
            return 'UNKNOWN';
        case SemanticType.CAR:
            return 'CAR';
        case SemanticType.TRUCK:
        case SemanticType.TRAILER:
            return 'TRAILER';
        case SemanticType.BUS:
            return 'BUS';
        case SemanticType.BICYCLE:
            return 'BICYCLE';
        case SemanticType.MOTORBIKE:
        case SemanticType.MOTORCYCLE:
            return 'MOTORCYCLE';
        case SemanticType.PEDESTRIAN:
            return 'PEDESTRIAN';
        case SemanticType.ANIMAL:
            return 'ANIMAL';
    }
}

function ObjectWithSemantic(props: { target?: TrackedObject }) {
    const { target } = props;
    return <div>{semanticText(target?.semantic ?? SemanticType.UNKNOWN)}</div>;
}

const Frame = styled.div({
    width: '100%',
    height: '100%',
    backgroundColor: '#101224',
    position: 'relative',
});

export type VisualizerViewProps = {
    engine: VisualizeEngine;
    mapFiles?: File[];
}

export function VisualizerView(props: VisualizerViewProps) {
    const { engine, mapFiles } = props;

    const messageCatalog = useMessageCatalog('universe');

    const [map, setMap] = useState<Lanelet2MapsGroup>();
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!mapFiles || mapFiles.length === 0) return;

        setLoading(true);
        (async () => {
            const map = await loadLanelet2Map(mapFiles);
            setMap(map);
            setLoading(false);
        })();
    }, [mapFiles]);

    return (
        <Frame>
            <AutowareViz engine={engine} messageCatalog={messageCatalog}>
                <Space dimension={3} initialCameraPosition={[-10, 0, 20]} enableOrbitControls={false}>
                    <OrbitControls makeDefault enableDamping={false} enabled={true} />
                    <ambientLight />
                    <pointLight position={[100, 100, 100]} />
                    <GroundGrid width={200000} height={200000} />
                    {map ? <Lanelet2Map map={map} /> : <></>}
                    <Coordinate frameId='map'>
                        <Coordinate frameId='base_link'>
                            <VehicleObject url={j6} rotateToAutoware={false} />
                            <Camera camMode={CamMode.tracking}
                                trackingCameraPosition={[-5, 0, 4]}
                                trackingTargetPosition={[10, 0, 0]}
                                trackingDelay={100}
                            />
                            <DetectedObjects />
                        </Coordinate>
                        <Path />
                        <Route />
                        <Trajectory />
                        <VirtualWalls />
                        <DirectionalTrackedObjects Component={ObjectWithSemantic} />
                        <PredictedPaths />
                    </Coordinate>
                </Space>
                <StyledRemoteClock />
            </AutowareViz>
            <Backdrop open={loading} style={{ zIndex: 100 }}>
                <Grid container flexDirection='column' width='100%' alignItems='center'>
                    <Grid>
                        <Typography color='wheat'>Loading...</Typography>
                    </Grid>
                    <Grid>
                        <CircularProgress color='primary' disableShrink />
                    </Grid>
                </Grid>
            </Backdrop>
        </Frame>
    );
}
